/**
 * 性能监控工具
 * 用于监控和分析经销商端的性能指标
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.renderTimes = new Map();
    this.apiTimes = new Map();
    this.memoryUsage = [];
    this.isEnabled = process.env.NODE_ENV === 'development';
  }

  /**
   * 开始性能测量
   * @param {string} name 测量名称
   */
  start(name) {
    if (!this.isEnabled) return;
    
    this.metrics.set(name, {
      startTime: performance.now(),
      startMemory: this.getMemoryUsage()
    });
  }

  /**
   * 结束性能测量
   * @param {string} name 测量名称
   * @returns {Object} 性能指标
   */
  end(name) {
    if (!this.isEnabled) return null;
    
    const metric = this.metrics.get(name);
    if (!metric) {
      console.warn(`Performance metric "${name}" not found`);
      return null;
    }

    const endTime = performance.now();
    const endMemory = this.getMemoryUsage();
    
    const result = {
      name,
      duration: endTime - metric.startTime,
      memoryDelta: endMemory - metric.startMemory,
      timestamp: new Date().toISOString()
    };

    this.metrics.delete(name);
    this.logMetric(result);
    
    return result;
  }

  /**
   * 测量组件渲染时间
   * @param {string} componentName 组件名称
   * @param {Function} renderFn 渲染函数
   */
  measureRender(componentName, renderFn) {
    if (!this.isEnabled) return renderFn();
    
    const startTime = performance.now();
    const result = renderFn();
    const endTime = performance.now();
    
    const renderTime = endTime - startTime;
    this.recordRenderTime(componentName, renderTime);
    
    if (renderTime > 16) { // 超过一帧的时间
      console.warn(`Slow render detected: ${componentName} took ${renderTime.toFixed(2)}ms`);
    }
    
    return result;
  }

  /**
   * 测量API调用时间
   * @param {string} apiName API名称
   * @param {Function} apiFn API调用函数
   */
  async measureApi(apiName, apiFn) {
    if (!this.isEnabled) return apiFn();
    
    const startTime = performance.now();
    try {
      const result = await apiFn();
      const endTime = performance.now();
      
      this.recordApiTime(apiName, endTime - startTime, 'success');
      return result;
    } catch (error) {
      const endTime = performance.now();
      this.recordApiTime(apiName, endTime - startTime, 'error');
      throw error;
    }
  }

  /**
   * 记录组件渲染时间
   * @param {string} componentName 组件名称
   * @param {number} time 渲染时间
   */
  recordRenderTime(componentName, time) {
    if (!this.renderTimes.has(componentName)) {
      this.renderTimes.set(componentName, []);
    }
    
    const times = this.renderTimes.get(componentName);
    times.push(time);
    
    // 只保留最近100次记录
    if (times.length > 100) {
      times.shift();
    }
  }

  /**
   * 记录API调用时间
   * @param {string} apiName API名称
   * @param {number} time 调用时间
   * @param {string} status 调用状态
   */
  recordApiTime(apiName, time, status) {
    if (!this.apiTimes.has(apiName)) {
      this.apiTimes.set(apiName, []);
    }
    
    const times = this.apiTimes.get(apiName);
    times.push({ time, status, timestamp: Date.now() });
    
    // 只保留最近100次记录
    if (times.length > 100) {
      times.shift();
    }
  }

  /**
   * 获取内存使用情况
   * @returns {number} 内存使用量（MB）
   */
  getMemoryUsage() {
    if (performance.memory) {
      return performance.memory.usedJSHeapSize / 1024 / 1024;
    }
    return 0;
  }

  /**
   * 记录内存使用情况
   */
  recordMemoryUsage() {
    if (!this.isEnabled) return;
    
    const usage = this.getMemoryUsage();
    this.memoryUsage.push({
      usage,
      timestamp: Date.now()
    });
    
    // 只保留最近1000次记录
    if (this.memoryUsage.length > 1000) {
      this.memoryUsage.shift();
    }
  }

  /**
   * 获取性能报告
   * @returns {Object} 性能报告
   */
  getReport() {
    const report = {
      renderTimes: {},
      apiTimes: {},
      memoryUsage: this.memoryUsage.slice(-10), // 最近10次内存记录
      timestamp: new Date().toISOString()
    };

    // 计算渲染时间统计
    for (const [componentName, times] of this.renderTimes.entries()) {
      if (times.length > 0) {
        report.renderTimes[componentName] = {
          count: times.length,
          average: times.reduce((a, b) => a + b, 0) / times.length,
          min: Math.min(...times),
          max: Math.max(...times),
          recent: times.slice(-5) // 最近5次
        };
      }
    }

    // 计算API时间统计
    for (const [apiName, records] of this.apiTimes.entries()) {
      if (records.length > 0) {
        const times = records.map(r => r.time);
        const successCount = records.filter(r => r.status === 'success').length;
        const errorCount = records.filter(r => r.status === 'error').length;
        
        report.apiTimes[apiName] = {
          count: records.length,
          successCount,
          errorCount,
          successRate: successCount / records.length,
          average: times.reduce((a, b) => a + b, 0) / times.length,
          min: Math.min(...times),
          max: Math.max(...times),
          recent: records.slice(-5) // 最近5次
        };
      }
    }

    return report;
  }

  /**
   * 打印性能报告
   */
  printReport() {
    if (!this.isEnabled) return;
    
    const report = this.getReport();
    console.group('🚀 Performance Report');
    
    // 渲染性能
    console.group('📊 Render Performance');
    Object.entries(report.renderTimes).forEach(([component, stats]) => {
      console.log(`${component}:`, {
        average: `${stats.average.toFixed(2)}ms`,
        count: stats.count,
        range: `${stats.min.toFixed(2)}ms - ${stats.max.toFixed(2)}ms`
      });
    });
    console.groupEnd();
    
    // API性能
    console.group('🌐 API Performance');
    Object.entries(report.apiTimes).forEach(([api, stats]) => {
      console.log(`${api}:`, {
        average: `${stats.average.toFixed(2)}ms`,
        successRate: `${(stats.successRate * 100).toFixed(1)}%`,
        count: stats.count
      });
    });
    console.groupEnd();
    
    // 内存使用
    if (report.memoryUsage.length > 0) {
      const currentMemory = report.memoryUsage[report.memoryUsage.length - 1];
      console.log('💾 Memory Usage:', `${currentMemory.usage.toFixed(2)}MB`);
    }
    
    console.groupEnd();
  }

  /**
   * 记录性能指标
   * @param {Object} metric 性能指标
   */
  logMetric(metric) {
    if (metric.duration > 100) { // 超过100ms的操作
      console.warn(`Slow operation: ${metric.name} took ${metric.duration.toFixed(2)}ms`);
    }
  }

  /**
   * 清除所有记录
   */
  clear() {
    this.metrics.clear();
    this.renderTimes.clear();
    this.apiTimes.clear();
    this.memoryUsage = [];
  }

  /**
   * 启用/禁用监控
   * @param {boolean} enabled 是否启用
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
  }
}

// 创建全局实例
const performanceMonitor = new PerformanceMonitor();

// 定期记录内存使用情况
if (typeof window !== 'undefined') {
  setInterval(() => {
    performanceMonitor.recordMemoryUsage();
  }, 5000); // 每5秒记录一次
}

export default performanceMonitor;
